import { useEffect, useState } from 'react';
import { fmtTs } from '../lib/globalTz';

type BannerSeverity = 'info' | 'success' | 'warning' | 'error';

interface BannerNotification {
  id: string;
  title: string;
  message: string;
  severity: BannerSeverity;
  isActive: boolean;
  startsAt: string;
  endsAt?: string | null;
  createdBy?: string;
  createdByEmail?: string;
  createdAt: string;
  updatedAt: string;
}

const defaultForm = {
  title: '',
  message: '',
  severity: 'info' as BannerSeverity,
  startsAt: '',
  endsAt: '',
  isActive: true,
};

export function MastersBannerNotifications() {
  const [banners, setBanners] = useState<BannerNotification[]>([]);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    const resp = await fetch('/api/masters/banner-notifications', {
      credentials: 'include',
    });

    if (!resp.ok) {
      setError(`Failed to load banners: HTTP ${resp.status}`);
      return;
    }

    const data = await resp.json() as { banners?: BannerNotification[] };
    setBanners(data.banners ?? []);
  }

  useEffect(() => {
    void load();
  }, []);

  function update<K extends keyof typeof form>(key: K, value: typeof form[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function reset() {
    setForm(defaultForm);
    setEditingId(null);
    setError('');
  }

  async function save() {
    setSaving(true);
    setError('');

    try {
      const payload = {
        ...form,
        startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : undefined,
        endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
      };

      const resp = await fetch(
        editingId
          ? `/api/masters/banner-notifications/${editingId}`
          : '/api/masters/banner-notifications',
        {
          method: editingId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        },
      );

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({} as { message?: string }));
        throw new Error(body.message ?? `Save failed: HTTP ${resp.status}`);
      }

      reset();
      await load();
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setSaving(false);
    }
  }

  async function deactivate(id: string) {
    if (!confirm('Deactivate this banner notification?')) return;

    const resp = await fetch(`/api/masters/banner-notifications/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!resp.ok) {
      setError(`Deactivate failed: HTTP ${resp.status}`);
      return;
    }

    await load();
  }

  function edit(b: BannerNotification) {
    setEditingId(b.id);
    setForm({
      title: b.title,
      message: b.message,
      severity: b.severity,
      startsAt: b.startsAt ? b.startsAt.slice(0, 16) : '',
      endsAt: b.endsAt ? b.endsAt.slice(0, 16) : '',
      isActive: b.isActive,
    });
  }

  return (
    <section className="bg-card rounded-2xl border border-border p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-bold text-foreground">Banner Notifications</h3>
          <p className="text-sm text-muted-foreground">
            Publish platform-wide information, warning, error, or success messages for all dashboard users.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 text-red-700 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border p-4 space-y-3">
          <h4 className="text-sm font-bold">
            {editingId ? 'Edit Banner' : 'Create Banner'}
          </h4>

          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Title</label>
            <input
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
              className="w-full border border-border bg-background rounded-lg px-3 py-2 text-sm"
              placeholder="Example: Scheduled Maintenance"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Message</label>
            <textarea
              value={form.message}
              onChange={(e) => update('message', e.target.value)}
              className="w-full border border-border bg-background rounded-lg px-3 py-2 text-sm min-h-[90px]"
              placeholder="Enter the message users should see..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1">Severity</label>
              <select
                value={form.severity}
                onChange={(e) => update('severity', e.target.value as BannerSeverity)}
                className="w-full border border-border bg-background rounded-lg px-3 py-2 text-sm"
              >
                <option value="info">Information</option>
                <option value="success">Success</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm mt-6">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => update('isActive', e.target.checked)}
              />
              Active
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1">Start Date/Time</label>
              <input
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => update('startsAt', e.target.value)}
                className="w-full border border-border bg-background rounded-lg px-3 py-2 text-sm"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Blank means immediately.</p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1">End Date/Time</label>
              <input
                type="datetime-local"
                value={form.endsAt}
                onChange={(e) => update('endsAt', e.target.value)}
                className="w-full border border-border bg-background rounded-lg px-3 py-2 text-sm"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Blank means no expiry.</p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
            >
              {saving ? 'Saving…' : editingId ? 'Update Banner' : 'Create Banner'}
            </button>

            {editingId && (
              <button
                onClick={reset}
                className="border border-border px-4 py-2 rounded-lg text-sm font-semibold"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-border p-4">
          <h4 className="text-sm font-bold mb-3">Existing Banners</h4>

          <div className="space-y-2 max-h-[460px] overflow-y-auto">
            {banners.length === 0 && (
              <div className="text-sm text-muted-foreground">
                No banner notifications configured.
              </div>
            )}

            {banners.map((b) => (
              <div key={b.id} className="rounded-lg border border-border p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-bold text-foreground">{b.title}</div>
                    <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{b.message}</div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                    b.severity === 'error' ? 'bg-red-100 text-red-700' :
                    b.severity === 'warning' ? 'bg-amber-100 text-amber-700' :
                    b.severity === 'success' ? 'bg-green-100 text-green-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {b.severity.toUpperCase()}
                  </span>
                </div>

                <div className="text-[11px] text-muted-foreground mt-2">
                  Active: {b.isActive ? 'Yes' : 'No'}
                  {b.endsAt ? ` · Ends: ${fmtTs(b.endsAt)}` : ' · No expiry'}
                </div>

                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => edit(b)}
                    className="text-xs border border-border px-3 py-1 rounded-lg"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deactivate(b.id)}
                    className="text-xs border border-red-300 text-red-700 px-3 py-1 rounded-lg"
                  >
                    Deactivate
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
